import { useState } from "react";
import Masonry from "react-responsive-masonry";
import { Button } from "../../components/Button/Button.tsx";
import { BaseImage } from "../../components/Content/BaseImage.tsx";
import { ThumbnailContent } from "../../components/Content/ThumbnailContent.tsx";
import { PimentoStar } from "../../components/Icon/assets/PimentoStar.tsx";
import { IMAGE_RESOURCE_TYPE } from "../Board/constants.ts";
import { useRetrainModel } from "../Board/hooks/useRetrainModel.ts";
import { BLANK_PRESET } from "../boardState/constants.ts";
import { DEFAULT_STYLE_MODELS_PROPERTIES } from "../constants.ts";
import { useCreateBoard } from "../Home/HomeIndex/hooks/useCreateBoard.ts";
import { useRedirectToBoard } from "../hooks/useRedirectToBoard.ts";
import { useModel } from "./hooks/useModel.ts";
import { useRedirectToHomepageModels } from "./hooks/useRedirectToHomepageModels.ts";
import { ModelTrainDialog } from "./ModelTrainDialog.tsx";

export const ModelDescription = () => {
  const { model } = useModel();
  const redirectToBoard = useRedirectToBoard();

  // FIXME: The different redirections should be changed when the new routing is implemented at the project level
  const { createBoard } = useCreateBoard({
    onSuccess: (uuid) => {
      redirectToBoard({
        uuid,
        boardStatePreset: BLANK_PRESET,
        baseBoardState: {
          modelUuids: [model.uuid],
        },
      });
    },
  });

  const {
    isDisabled: isRetrainDisabled,
    isUserAllowed: isUserAllowedRetrain,
    modelRetrainStatus,
  } = useRetrainModel({
    modelUuid: model.uuid,
  });

  const {
    mutate: retrainForFreeMutate,
    isLoading: isRetrainForFreeLoading,
    isUserAllowed: isUserAllowedRetrainForFree,
  } = useRetrainModel({
    modelUuid: model.uuid,
    forFree: true,
  });

  const [
    isTrainingModelPurchaseDialogOpen,
    setIsTrainingModelPurchaseDialogOpen,
  ] = useState(false);

  const redirectToHomepageModels = useRedirectToHomepageModels(model.uuid);

  const thumbnailUrl =
    model.thumbnail_url ??
    DEFAULT_STYLE_MODELS_PROPERTIES[model.uuid]?.thumbnail_url;

  const trainingImagesUrls =
    DEFAULT_STYLE_MODELS_PROPERTIES[model.uuid]?.image_training_urls ??
    model.contents.map((image) => image.url);

  return (
    <div className="flex-row h-full text-primary">
      <div className="flex-col w-[350px] gap-2000 px-600 py-1000">
        <div>
          <Button
            onClick={redirectToHomepageModels}
            variant="tertiary"
            iconName="ChevronLeft"
            size="lg"
          >
            Back
          </Button>
        </div>
        <div className="flex-col px-200 gap-600">
          <div className="flex-col gap-400">
            <div className="h-[128px] w-[128px] rounded-200 overflow-hidden">
              {thumbnailUrl ? (
                <ThumbnailContent
                  resourceType={IMAGE_RESOURCE_TYPE}
                  src={thumbnailUrl}
                  className="w-full h-full"
                />
              ) : (
                <div className="flex-col-center h-full bg-surface-emphasis-primary-rest">
                  <PimentoStar size={70} className="fill-button-primary-rest" />
                </div>
              )}
            </div>
            <div className="heading-4xl">{model.name}</div>
          </div>
          <div className="flex-col w-full gap-100">
            <Button
              className="w-fit"
              size="lg"
              onClick={() => {
                createBoard();
              }}
            >
              Use in new project
            </Button>
          </div>
          {modelRetrainStatus !== "upToDate" && isUserAllowedRetrain && (
            <>
              <Button
                onClick={() =>
                  setIsTrainingModelPurchaseDialogOpen(
                    !isTrainingModelPurchaseDialogOpen,
                  )
                }
                disabled={isRetrainDisabled}
                size="sm"
                variant="secondary"
              >
                Retrain model
              </Button>
              <ModelTrainDialog
                isOpen={isTrainingModelPurchaseDialogOpen}
                onOpenChange={(open) => {
                  setIsTrainingModelPurchaseDialogOpen(open);
                }}
                modelUuid={model.uuid}
                trainAction="retrain"
              />
            </>
          )}
          {modelRetrainStatus !== "upToDate" && isUserAllowedRetrainForFree && (
            <Button
              onClick={retrainForFreeMutate}
              disabled={isRetrainDisabled}
              loading={isRetrainForFreeLoading}
              size="sm"
              variant="error"
            >
              ADMIN-ONLY: Retrain model for free
            </Button>
          )}
        </div>
      </div>
      <div className="flex-fill bg-input-surface-rest p-1000 overflow-y-auto">
        <div className="flex-col gap-400">
          <Masonry columnsCount={4} gutter="8px">
            {trainingImagesUrls.map((url) => (
              <BaseImage key={url} src={url} />
            ))}
          </Masonry>
        </div>
      </div>
    </div>
  );
};
